import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import UserAvatar from '../UserAvatar';
import BotAvatar, { botState } from '../bots/BotAvatar';
import BotName from '../bots/BotName';
import { useAuth } from '../../contexts/AuthContext';
import { useMyBotsQuery, useTeamsQuery } from '../../hooks/queries';
import { useAssignTaskBot } from '../../hooks/mutations';
import { tasksApi } from '../../services/api';

const Row = ({ selected, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm transition-colors ${
      selected ? 'bg-accent text-foreground' : 'text-foreground/80 hover:bg-accent/60 hover:text-foreground'}`}>
    <span className="flex items-center gap-2 min-w-0">{children}</span>
    {selected && <Check className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
  </button>
);

/**
 * Who works on a task, picked right on a card (like Linear and Jira): the
 * avatars of the people and the bot, and a click opens the list. Bots come
 * first, as handing a task to a bot is one click this way.
 *
 * onChange(patch) lets the board update the card before the server answers.
 */
const AssigneePicker = ({ task, project, onChange, alwaysVisible = false }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const { data: teams = [] } = useTeamsQuery();
  const { data: mine } = useMyBotsQuery();
  const assignBot = useAssignTaskBot();

  const teamId = task.team_id || project?.team_id || null;
  const team = teams.find((x) => x.id === teamId);
  const members = useMemo(() => [
    ...(user ? [{ user_id: user.id, full_name: user.full_name, email: user.email, avatar_url: user.avatar_url, isSelf: true }] : []),
    ...(team?.members || []).filter((m) => m.user_id !== user?.id),
  ], [team, user]);
  const byId = useMemo(() => Object.fromEntries(
    teams.flatMap((x) => x.members || []).concat(user ? [{ ...user, user_id: user.id }] : []).map((m) => [m.user_id, m])), [teams, user]);
  const bots = (mine?.bots || []).filter((b) => (teamId
    ? b.owner_type === 'team' && b.owner_id === teamId
    : b.owner_type === 'user' && b.owner_id === task.user_id));
  const bot = (mine?.bots || []).find((b) => b.id === task.assigned_bot_id);
  const people = (task.assigned_to || []).map((id) => byId[id]).filter(Boolean);
  const empty = !bot && people.length === 0;

  const togglePerson = async (userId) => {
    const before = task.assigned_to || [];
    const next = before.includes(userId) ? before.filter((id) => id !== userId) : [...before, userId];
    onChange?.({ assigned_to: next });
    try {
      await tasksApi.update(task.id, { assigned_to: next });
    } catch {
      onChange?.({ assigned_to: before });
      toast.error(t('common.error'));
    }
  };
  const toggleBot = (botId) => {
    const before = task.assigned_bot_id || null;
    const next = before === botId ? null : botId;
    onChange?.({ assigned_bot_id: next });
    assignBot.mutate({ taskId: task.id, botId: next }, {
      onError: () => { onChange?.({ assigned_bot_id: before }); toast.error(t('common.error')); },
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}
          aria-label={t('tasks.assign')} title={t('tasks.assign')} data-testid="card-assignee"
          className={`ml-auto shrink-0 inline-flex items-center -space-x-1.5 rounded-full p-0.5 hover:bg-accent transition-opacity ${
            empty && !alwaysVisible && !open ? 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100' : ''}`}>
          {empty ? (
            <span className="w-6 h-6 rounded-full border border-dashed border-muted-foreground/50 flex items-center justify-center text-muted-foreground">
              <UserPlus className="w-3.5 h-3.5" />
            </span>
          ) : (
            <>
              {bot && <BotAvatar bot={bot} size="sm" state={botState(bot)} />}
              {people.slice(0, bot ? 2 : 3).map((m) => (
                <UserAvatar key={m.user_id} name={m.full_name || m.email} avatarUrl={m.avatar_url} size="sm" className="ring-2 ring-popover" />
              ))}
              {people.length > (bot ? 2 : 3) && (
                <span className="w-6 h-6 rounded-full bg-muted text-[11px] font-medium text-muted-foreground flex items-center justify-center ring-2 ring-popover">
                  +{people.length - (bot ? 2 : 3)}
                </span>
              )}
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-60 p-0 py-1 max-h-80 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {bots.length > 0 && (
          <>
            <div className="px-3 pt-1.5 pb-1 text-xs font-medium text-muted-foreground">{t('bots.sidebar_title')}</div>
            {bots.map((b) => (
              <Row key={b.id} selected={task.assigned_bot_id === b.id} onClick={() => toggleBot(b.id)} testid="assignee-bot">
                <BotAvatar bot={b} size="sm" state={botState(b)} />
                <BotName bot={b} />
              </Row>
            ))}
            <div className="my-1 border-t border-border" />
          </>
        )}
        <div className="px-3 pt-1.5 pb-1 text-xs font-medium text-muted-foreground">{t('tasks.assignee')}</div>
        {members.map((m) => (
          <Row key={m.user_id} selected={(task.assigned_to || []).includes(m.user_id)} onClick={() => togglePerson(m.user_id)} testid="assignee-person">
            <UserAvatar name={m.full_name || m.email} avatarUrl={m.avatar_url} size="xs" />
            <span className="truncate">{m.isSelf ? t('tasks.me') : (m.full_name || m.email)}</span>
          </Row>
        ))}
      </PopoverContent>
    </Popover>
  );
};

export default AssigneePicker;
